import { createChart, CrosshairMode, IChartApi, ISeriesApi, LineData, LineSeries, Time } from "lightweight-charts";
import moment from "moment-timezone";
import React, { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { Col, Row } from "reactstrap";

interface ChartData {
  time: number;
  value: number;
}

interface IProps {
  data: any[];
  tokenSymbols?: {
    symbol: string[];
    decimal: string[];
  };
}

const LineChart = ({ data, tokenSymbols = { symbol: ['', ''], decimal: ['4', '4'] } }: IProps) => {
  const { t, i18n } = useTranslation();
  const { layoutModeType } = useSelector((state: any) => state.Layout);
  const _isDark: boolean = layoutModeType === "dark";
  const CLIENT_TIME_ZONE = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const chartRef = useRef<IChartApi | null>(null);
  const lineSeriesRef = useRef<ISeriesApi<"Line"> | null>(null);

  const [statusChart, setStatusChart] = useState<any>({ value: 0, change: 0 });

  const calculateChartStats = (chartData: ChartData[]) => {
    if (!chartData.length) {
      return { value: 0, change: 0, color: "#80808080" };
    }
    const latest = chartData[chartData.length - 1];
    const first = chartData[0];
    const change = ((latest.value - first.value) / first.value) * 100;
    return {
      value: latest.value,
      change,
      color: change >= 0 ? "#26a69a80" : "#ef535080",
    };
  };

  useEffect(() => {
    if (!chartContainerRef.current) return;

    moment.locale(i18n.language);

    chartRef.current = createChart(chartContainerRef.current, {
      width: chartContainerRef.current.clientWidth,
      height: 500,
      layout: {
        background: { color: _isDark ? "#1E252B" : "#FFFFFF" },
        textColor: _isDark ? "#D1D4DC" : "#2A2E39",
        fontSize: 12,
      },
      grid: {
        vertLines: { color: _isDark ? "#2A2E39" : "#F6F6F6" },
        horzLines: { color: _isDark ? "#2A2E39" : "#F6F6F6" },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          visible: true,
          labelVisible: false,
        },
        horzLine: {
          color: _isDark ? "#D1D4DC" : "#2A2E39",
          width: 1,
          style: 1,
          labelBackgroundColor: _isDark ? "#2A2E39" : "#E0E3EB",
        },
      },
      rightPriceScale: {
        borderColor: _isDark ? "#2A2E39" : "#E0E3EB",
        textColor: _isDark ? "#D1D4DC" : "#2A2E39",
      },
      timeScale: {
        borderColor: _isDark ? "#2A2E39" : "#E0E3EB",
        timeVisible: true,
        secondsVisible: true,
        tickMarkFormatter: (time: number) => {
          const date = moment.unix(time).tz(CLIENT_TIME_ZONE);
          return date.format("MM/DD HH:mm");
        },
      },
    });

    lineSeriesRef.current = chartRef.current.addSeries(LineSeries, {
      color: "#26a69a",
      lineWidth: 2,
      priceFormat: {
        precision: Number(tokenSymbols?.decimal?.[1] || 6),
        minMove: 1 / Math.pow(10, Number(tokenSymbols?.decimal?.[1] || 6)),
      },
    });

    const tooltip = document.createElement("div");
    tooltip.setAttribute("id", "time-tooltip");
    tooltip.style.position = "absolute";
    tooltip.style.bottom = "16px";
    tooltip.style.transform = "translateX(-50%)";
    tooltip.style.color = _isDark ? "#D1D4DC" : "#2A2E39";
    tooltip.style.borderRadius = "6px";
    tooltip.style.boxShadow = "var(--shadow-sm)";
    tooltip.style.padding = "2px 8px";
    tooltip.style.fontSize = "13px";
    tooltip.style.pointerEvents = "none";
    tooltip.style.zIndex = "1000";
    chartContainerRef.current.appendChild(tooltip);

    const xAxisLabel = document.createElement("div");
    const offset = moment().tz(CLIENT_TIME_ZONE).format("Z");
    xAxisLabel.textContent = `UTC ${offset}`;
    xAxisLabel.style.position = "absolute";
    xAxisLabel.style.bottom = "20px";
    xAxisLabel.style.right = "4px";
    xAxisLabel.style.fontSize = "12px";
    xAxisLabel.style.fontWeight = "500";
    xAxisLabel.style.color = "#000";
    xAxisLabel.style.pointerEvents = "none";
    xAxisLabel.style.zIndex = "999";
    chartContainerRef.current.appendChild(xAxisLabel);

    chartRef.current?.subscribeCrosshairMove((param) => {
      const tooltipEl = document.getElementById("time-tooltip");
      if (!tooltipEl || !param.time || !param.point) {
        if (tooltipEl) tooltipEl.style.display = "none";
        return;
      }

      tooltip.style.background = _isDark ? "#2A2E39" : "#E0E3EB";
      tooltip.style.border = "1px solid #ccc";
      const timestamp = Number(param.time);
      const localTimeStr = moment.unix(timestamp).tz(moment.tz.guess()).format(`MM/DD HH:mm`);
      tooltipEl.textContent = localTimeStr;
      tooltipEl.style.display = "block";
      tooltipEl.style.transform = `translateX(${param.point.x}px)`;

      const lineData = param.seriesData.get(lineSeriesRef.current!) as LineData;
      if (lineData) {
        setStatusChart({ value: lineData.value, change: statusChart.change });
      }
    });

    return () => {
      chartRef.current?.remove();
      chartRef.current = null;
      lineSeriesRef.current = null;
    };
  }, []);

  useEffect(() => {
    if (!chartRef.current) return;

    chartRef.current.applyOptions({
      layout: {
        background: { color: _isDark ? "#1E252B" : "#FFFFFF" },
        textColor: _isDark ? "#D1D4DC" : "#2A2E39",
      },
      grid: {
        vertLines: { color: _isDark ? "#2A2E39" : "#F6F6F6" },
        horzLines: { color: _isDark ? "#2A2E39" : "#F6F6F6" },
      },
      crosshair: {
        mode: CrosshairMode.Normal,
        vertLine: {
          color: _isDark ? "#D1D4DC" : "#2A2E39",
          labelBackgroundColor: _isDark ? "#2A2E39" : "#E0E3EB",
        },
        horzLine: {
          color: _isDark ? "#D1D4DC" : "#2A2E39",
          labelBackgroundColor: _isDark ? "#2A2E39" : "#E0E3EB",
        },
      },
      rightPriceScale: {
        borderColor: _isDark ? "#2A2E39" : "#E0E3EB",
        textColor: _isDark ? "#D1D4DC" : "#2A2E39",
      },
    });
  }, [_isDark]);

  useEffect(() => {
    if (!lineSeriesRef.current || !data) return;

    // Remove duplicates and sort data by timestamp
    const uniqueData = data.reduce((acc: ChartData[], curr) => {
      const exists = acc.find(item => item.time === curr.time);
      if (!exists) {
        acc.push(curr);
      }
      return acc;
    }, []);

    const sortedData: any = uniqueData.sort((a, b) => a.time - b.time);
    lineSeriesRef.current.setData(sortedData);
    const stats = calculateChartStats(sortedData);
    setStatusChart(stats);
  }, [data]);

  useEffect(() => {
    if (!chartRef.current || !chartContainerRef.current) return;

    const handleResize = (entries: ResizeObserverEntry[]) => {
      const { width, height } = entries[0].contentRect;
      try {
        chartRef.current?.applyOptions({ width, height });
      } catch (error) {
        console.error("Error updating chart size:", error);
      }
    };

    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(chartContainerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  return (
    <div>
      <div className="position-relative">
        <Row
          className="w-full align-items-center ps-1 position-absolute"
          style={{ zIndex: 2, top: '2px', left: '10px', pointerEvents: 'none' }}
        >
          <Col sm={12} md={12} xl={12} xxl={12} className="flex flex-wrap gap-2">
            <div className="point-o">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 18 18" width="18" height="18" fill="none">
                <circle fill="currentColor" cx="9" cy="9" r="4"></circle>
              </svg>
            </div>
            <div className="flex gap-1">
              <span className="text-gray-400 fs-13">{t("Label_Value")}: </span>
              <span style={{ color: statusChart?.change >= 0 ? "#26a69a" : "#ef5350" }}>
                {statusChart?.value?.toFixed(Number(tokenSymbols?.decimal?.[1] || 6)) || "-"}
              </span>
            </div>
            <div className="flex gap-1">
              <span className="text-gray-400 fs-13">{t("Label_Change")}: </span>
              <span style={{ color: statusChart?.change >= 0 ? "#26a69a" : "#ef5350" }}>
                {statusChart?.change >= 0 ? "+" : ""}{statusChart?.change?.toFixed(2) || 0}%
              </span>
            </div>
          </Col>
        </Row>
        <div style={{ position: "relative", display: "flex" }}>
          <div style={{ flex: 1 }}>
            <div
              ref={chartContainerRef}
              style={{
                width: "100%",
                height: "500px",
                minHeight: "500px",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(LineChart);